import { CloseApproachEvent } from '../types/satellite';

interface CloseApproachPanelProps {
  events: CloseApproachEvent[];
  thresholdKm: number;
  horizonSec: number;
  maxDisplayEvents?: number;
}

export function CloseApproachPanel({ 
  events, 
  thresholdKm, 
  horizonSec,
  maxDisplayEvents = 200 
}: CloseApproachPanelProps) {
  
  const displayEvents = events.slice(0, maxDisplayEvents);
  const hasMoreEvents = events.length > maxDisplayEvents;
  
  const formatTime = (isoString: string): string => {
    const date = new Date(isoString);
    return date.toLocaleTimeString('en-US', { 
      hour12: false,
      timeZone: 'UTC',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };
  
  const formatDate = (isoString: string): string => {
    const date = new Date(isoString);
    const now = new Date();
    const diffMs = date.getTime() - now.getTime();
    const diffSec = Math.round(diffMs / 1000);
    
    if (diffSec < 60) {
      return `in ${diffSec}s`;
    } else if (diffSec < 3600) {
      return `in ${Math.round(diffSec / 60)}m`;
    } else {
      return `in ${Math.round(diffSec / 3600)}h`;
    }
  };
  
  const getSeverityColor = (distanceKm: number): string => {
    if (distanceKm < 1) return 'text-red-400';
    if (distanceKm < 5) return 'text-orange-400';
    if (distanceKm < 10) return 'text-yellow-400';
    return 'text-green-400';
  };
  
  const getSeverityIcon = (distanceKm: number): string => {
    if (distanceKm < 1) return '🚨';
    if (distanceKm < 5) return '⚠️';
    if (distanceKm < 10) return '⚡';
    return 'ℹ️';
  };
  
  return (
    <div className="p-4 bg-black/80 text-white text-sm min-h-[200px] max-h-[400px] overflow-y-auto">
      <div className="font-semibold mb-3 flex items-center justify-between">
        <span className="text-base">
          Close Approaches (≤ {thresholdKm} km within {Math.round(horizonSec / 60)}min)
        </span>
        {events.length > 0 && (
          <span className="text-sm opacity-70">
            {events.length} total events
          </span>
        )}
      </div>
      
      {events.length === 0 && (
        <div className="opacity-70 py-4">
          Run "Scan CA" to analyze the next few minutes for potential close approaches 
          (TLE-based rough estimates).
        </div>
      )}
      
      {displayEvents.map((event) => {
        const severityColor = getSeverityColor(event.dKm);
        const severityIcon = getSeverityIcon(event.dKm);
        
        return (
          <div 
            key={`${event.i}-${event.j}-${event.t}`}
            className="py-2 px-2 mb-2 border border-white/20 rounded-lg hover:bg-white/5 transition-colors"
          >
            <div className="flex items-center justify-between gap-4">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-sm">{severityIcon}</span>
                  <span className="opacity-70 text-sm">
                    {formatTime(event.t)} ({formatDate(event.t)})
                  </span>
                </div>
                <div className="flex flex-wrap items-center gap-1">
                  <span className="text-emerald-300 font-medium">{event.a}</span>
                  <span className="opacity-70">vs</span>
                  <span className="text-sky-300 font-medium">{event.b}</span>
                </div>
              </div>
              <div className={`font-bold text-lg whitespace-nowrap ${severityColor}`}>
                {event.dKm} km
              </div>
            </div>
          </div>
        );
      })}
      
      {hasMoreEvents && (
        <div className="opacity-70 mt-3 py-2 text-center border-t border-white/20">
          <div className="text-sm">Showing first {maxDisplayEvents} of {events.length} events.</div>
          <div className="text-xs mt-1">Export CA data to see all results.</div>
        </div>
      )}
      
      {events.length > 0 && (
        <div className="mt-3 pt-3 border-t border-white/30 text-xs opacity-70 space-y-1">
          <div className="font-medium text-yellow-300">⚠️ TLE-based estimates only - not suitable for operational collision avoidance</div>
          <div className="flex flex-wrap gap-4">
            <span>🚨 Critical: &lt;1km</span>
            <span>⚠️ High: &lt;5km</span>
            <span>⚡ Medium: &lt;10km</span>
          </div>
        </div>
      )}
    </div>
  );
}